import React, { useState } from 'react';
import { Box, LinearProgress, makeStyles } from '@material-ui/core';
import { ExportButton, List, Pagination, useListContext } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { selectAnc } from '../../redux/actions/ancs';
import RightInfoDrawer from '../drawer/RightInfoDrawer';
import { SOURCE_BOARDS_V2 } from '../../providers/resources';
import { ancsV2 } from '../../pages/paths';
import ActionList from '../avatarAnc/actions';
import { EnabledColumns } from '../avatarAnc/TableView1';
import { ViewMode } from '../avatarAnc/ViewToggle';
import MapAvatarsV2 from '../MapAvatarsv2/MapAvatarsV2';
import { useIsMobile } from '../../hooks/useIsMobile';
import DataSection from '../avatarAnc/DataSection';
import DataGridCardCustom from '../UI/DataGridCard/indexBoards';
import ListFilterV2 from './filters/ListFilterV2';
import BoardV2Card from './BoardV2Card';
import BoardV2Table from './BoardV2Table';
import { BREAKPOINTS } from '../../theme/breakpoints';
// Shared list chrome: turns the react-admin toolbar into the white card the other
// lists use, and whitens the table body. Every other list page imports it too.
import '../../styles/list.css';
import { PAGE_MAX_WIDTH } from '../../theme/crmColors';

const ListPagination = (props: any) => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

// Same page gutter as the v1 ANC list (its `pageContainer`), so the two screens line up.
const useStyles = makeStyles(() => ({
    pageContainer: {
        display: 'grid',
        gap: 16,
        padding: 24,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        boxSizing: 'border-box' as const,
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        minHeight: '100%',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gap: 8,
            padding: 8,
        },
    },
    mapContainer: {
        display: 'flex',
        height: '100%',
        minHeight: 0,
    },
}));

const listProps = {
    resource: SOURCE_BOARDS_V2,
    hasCreate: false,
    hasEdit: false,
    hasList: true,
    hasShow: false,
    basePath: ancsV2,
    sort: { field: 'creationDate', order: 'DESC' },
    perPage: 50,
    filter: {
        deleted: false,
    },
};

const LoadingBar = () => {
    const { loading } = useListContext();
    return loading ? (
        <LinearProgress style={{ position: 'absolute', top: -10, left: 0, right: 0, zIndex: 1000 }} />
    ) : null;
};

/**
 * rikutec_board_v2_view (GET /rikutec/source_boards_v2) as cards, a table or a map, like the v1 board
 * it is meant to replace. Each card unfolds onto the form detail, so the 112 columns of the view stay
 * reachable without an unreadable line.
 */
const BoardV2List: React.FC<{ viewMode?: ViewMode }> = ({ viewMode = 'cards' }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    // On a phone the table does not fit: same fallback to the cards as the v1 board.
    const effectiveViewMode = isMobile && viewMode === 'table' ? 'cards' : viewMode;

    const [enabledColumns, setEnabledColumns] = useState<EnabledColumns>({
        processingCapacity: false,
        sludgeStorageVolume: false,
        totalUsefulVolume: false,
        model: false,
        currentState: true,
    });
    const toggleColumn = (column: string, enabled: boolean) =>
        setEnabledColumns({ ...enabledColumns, [column]: enabled });

    // Same history panel as the v1 board: the selected card goes through redux and
    // RightInfoDrawer reads its traceability notes by avatarId, which v2 also carries.
    const selected: any = useSelector((state: any) => state?.rikutec?.ancs?.selected || {});
    const panelOpen = selected && Object.keys(selected).length > 0;
    const handleClosePanel = () => dispatch(selectAnc({}));

    return (
        <Box className={`list ${classes.pageContainer}`}>
            <DataSection />

            {effectiveViewMode === 'map' ? (
                <Box className={classes.mapContainer}>
                    <MapAvatarsV2
                        mapHeight='100%'
                        resource={SOURCE_BOARDS_V2}
                        initialLocation={{ latitude: 48.8589506, longitude: 2.2768488 }}
                    />
                </Box>
            ) : (
                <List
                    component='div'
                    {...listProps}
                    pagination={<ListPagination />}
                    filters={<ListFilterV2 />}
                    actions={
                        <ActionList
                            viewMode={effectiveViewMode}
                            toggleColumn={toggleColumn}
                            enabledColumns={enabledColumns}
                            extraActions={<ExportButton />}
                        />
                    }
                    bulkActionButtons={false}
                    empty={false}
                >
                    <Box style={{ display: 'relative' }}>
                        <LoadingBar />

                        {effectiveViewMode === 'table' ? (
                            <BoardV2Table enabledColumns={enabledColumns} />
                        ) : (
                            <DataGridCardCustom CardComponent={BoardV2Card} />
                        )}

                        {panelOpen && (
                            <RightInfoDrawer open={panelOpen} onClose={handleClosePanel} selected={selected} />
                        )}
                    </Box>
                </List>
            )}
        </Box>
    );
};

export default BoardV2List;
